import React from 'react';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { AxiosError } from 'axios';
import { handleAxiosError, IError } from 'utils/handleAxiosError';
import {
  EventStandardContainer,
  EventStandardContainerData,
} from 'components/EventStandardContainer';
import { ProfileInfo } from 'containers/ProfileInfo';
import { fetchCommonEventData } from 'api/events';
import { fetchProfileInfo } from 'api/people';
import { UserProfileProps } from 'components/User/UserProfile';
import { AuthorSectionProps, SpeakerChairSectionProps } from 'components/User/ContributionSection';

export type ProfilePageProps = InferGetStaticPropsType<typeof getStaticProps>;

const ProfilePage: NextPage<ProfilePageProps> = ({ data, error }) => (
  <EventStandardContainer
    data={data}
    error={error}
    Component={ProfileInfo}
    fetchEmbargoedPageData={
      data?.event.embargoed ? () => fetchProfileInfo(data.event._id, data.userId!) : undefined
    }
  />
);

const dummyfyProfilePageData = (profile: ResolvedPromise<ReturnType<typeof fetchProfileInfo>>) => ({
  ...profile,
  author: profile?.author.slice(0, 3) || [],
  chair: profile?.chair.slice(0, 3) || [],
  speaker: profile?.speaker.slice(0, 3) || [],
});

export const getStaticProps: GetStaticProps<
  {
    data?: EventStandardContainerData &
      UserProfileProps & {
        userId?: string;
        author?: AuthorSectionProps[];
        speaker?: SpeakerChairSectionProps[];
        chair?: SpeakerChairSectionProps[];
      };
    error?: IError;
  },
  { eventId: string; userId: string }
> = async ctx => {
  try {
    const eventId = ctx.params?.eventId as string;
    const userId = ctx.params?.userId as string;

    const { eventInfo, tabsConfig } = await fetchCommonEventData(eventId);

    const userProfileInfo = eventInfo.event.embargoed
      ? await dummyfyProfilePageData(await fetchProfileInfo(eventId, userId))
      : await fetchProfileInfo(eventId, userId);

    return {
      props: {
        data: {
          userId,
          ...eventInfo,
          tabsConfig,
          ...userProfileInfo,
        },
      },
      revalidate: 1,
    };
  } catch (err) {
    const error = handleAxiosError(err as AxiosError);

    return {
      props: { error },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' });

export default ProfilePage;
